import { useEffect, useMemo, useRef, useState } from "react";

import type { LearnerSetup } from "../appModel";
import {
  installWebContentPackage,
  loadAllInstalledWebContentPackages,
  loadInstalledWebContentPackage,
  loadWebContentCatalog,
  resolveWebScenario,
} from "./webContentRepository";
import type {
  LearningDomain,
  LearningLevel,
  ResolvedWebScenario,
  SignedWebContentCatalog,
  WebContentCatalogEntry,
  InstalledWebContentPackage,
} from "./webContentTypes";

interface LibrarySelection {
  targetLanguage: string;
  level: LearningLevel | null;
  domain: LearningDomain | null;
  scenarioId: string | null;
}

interface PendingScenario {
  entry: WebContentCatalogEntry;
  scenarioId: string;
}

const STORAGE_KEY = "wordinarius_echo.web.content_selection.v1";

function persistSelection(selection: LibrarySelection): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
  } catch {
    // Selection persistence is optional; the active in-memory session stays usable.
  }
}

function initialSelection(setup: LearnerSetup | null): LibrarySelection {
  const empty: LibrarySelection = {
    targetLanguage: setup?.targetLanguage || "",
    level: null,
    domain: null,
    scenarioId: null,
  };
  if (!setup || typeof window === "undefined") {
    return empty;
  }
  try {
    const value = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "null") as Partial<LibrarySelection> | null;
    if (value?.targetLanguage !== setup.targetLanguage) {
      return empty;
    }
    return {
      targetLanguage: setup.targetLanguage,
      level: value.level || null,
      domain: value.domain || null,
      scenarioId: value.scenarioId || null,
    };
  } catch {
    return empty;
  }
}

export function useWebContentLibrary(setup: LearnerSetup | null, enabled: boolean) {
  const [catalog, setCatalog] = useState<SignedWebContentCatalog | null>(null);
  const [selection, setSelectionState] = useState<LibrarySelection>(() => initialSelection(setup));
  const [activeScenario, setActiveScenario] = useState<ResolvedWebScenario | null>(null);
  const [pendingScenario, setPendingScenario] = useState<PendingScenario | null>(null);
  const [installedPackages, setInstalledPackages] = useState<InstalledWebContentPackage[]>([]);
  const [installedStatus, setInstalledStatus] = useState<"idle" | "loading" | "ready">("idle");
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [installing, setInstalling] = useState(false);
  const [installProgress, setInstallProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const installRequestId = useRef(0);

  const updateSelection = (next: LibrarySelection) => {
    setSelectionState(next);
    persistSelection(next);
  };

  useEffect(() => {
    if (!setup) {
      setSelectionState(initialSelection(null));
      setActiveScenario(null);
      setPendingScenario(null);
      setInstalledPackages([]);
      setInstalledStatus("idle");
      return;
    }
    setSelectionState((current) => {
      if (current.targetLanguage === setup.targetLanguage) {
        return current;
      }
      const next = initialSelection(setup);
      persistSelection(next);
      return next;
    });
    setActiveScenario(null);
    setPendingScenario(null);
    setInstalledPackages([]);
    setInstalledStatus("idle");
  }, [setup?.targetLanguage]);

  useEffect(() => {
    if (!enabled || !setup) {
      return;
    }
    let active = true;
    setStatus("loading");
    setInstalledStatus("loading");
    setError(null);
    void loadWebContentCatalog()
      .then(async (nextCatalog) => {
        if (!active) {
          return;
        }
        setCatalog(nextCatalog);
        window.setTimeout(() => {
          void loadAllInstalledWebContentPackages(nextCatalog, setup.targetLanguage)
            .then((nextInstalledPackages) => {
              if (active) {
                setInstalledPackages(nextInstalledPackages);
              }
            })
            .catch(() => {
              if (active) {
                setInstalledPackages([]);
              }
            })
            .finally(() => {
              if (active) {
                setInstalledStatus("ready");
              }
            });
        }, 0);
        const stored = initialSelection(setup);
        if (!stored.level || !stored.domain || !stored.scenarioId) {
          setStatus("ready");
          return;
        }
        const entry = nextCatalog.entries.find((item) => (
          item.targetLanguage === setup.targetLanguage
          && item.level === stored.level
          && item.domain === stored.domain
        ));
        if (!entry) {
          updateSelection({ targetLanguage: setup.targetLanguage, level: null, domain: null, scenarioId: null });
          setStatus("ready");
          return;
        }
        const installed = await loadInstalledWebContentPackage(entry);
        if (active && installed) {
          setActiveScenario(resolveWebScenario(installed, stored.scenarioId, setup.supportLanguage));
        }
        if (active) {
          if (!installed) {
            updateSelection({ ...stored, scenarioId: null });
          }
          setStatus("ready");
        }
      })
      .catch(() => {
        if (active) {
          setStatus("error");
          setInstalledStatus("ready");
          setError("catalog_load_failed");
        }
      });
    return () => { active = false; };
  }, [enabled, loadAttempt, setup?.supportLanguage, setup?.targetLanguage]);

  const entries = useMemo(() => (
    catalog?.entries.filter((entry) => entry.targetLanguage === setup?.targetLanguage) || []
  ), [catalog, setup?.targetLanguage]);

  const chooseLevel = (level: LearningLevel) => {
    if (!setup) return;
    updateSelection({ targetLanguage: setup.targetLanguage, level, domain: null, scenarioId: null });
    setActiveScenario(null);
  };
  const chooseDomain = (domain: LearningDomain) => {
    if (!setup) return;
    updateSelection({ ...selection, targetLanguage: setup.targetLanguage, domain, scenarioId: null });
    setActiveScenario(null);
  };
  const openLevelPicker = () => {
    if (!setup) return;
    updateSelection({ targetLanguage: setup.targetLanguage, level: null, domain: null, scenarioId: null });
    setActiveScenario(null);
  };
  const openDomainPicker = () => {
    if (!setup) return;
    updateSelection({
      targetLanguage: setup.targetLanguage,
      level: selection.level,
      domain: null,
      scenarioId: null,
    });
    setActiveScenario(null);
  };
  const openScenarioPicker = () => {
    if (!setup) return;
    updateSelection({ ...selection, targetLanguage: setup.targetLanguage, scenarioId: null });
    setActiveScenario(null);
  };
  const back = () => {
    if (!setup) return;
    if (activeScenario || selection.scenarioId) {
      updateSelection({ ...selection, scenarioId: null });
      setActiveScenario(null);
      return;
    }
    if (selection.domain) {
      updateSelection({ ...selection, domain: null, scenarioId: null });
      return;
    }
    updateSelection({ targetLanguage: setup.targetLanguage, level: null, domain: null, scenarioId: null });
  };
  const openScenario = async (entry: WebContentCatalogEntry, scenarioId: string) => {
    if (!setup) return;
    setError(null);
    const installed = await loadInstalledWebContentPackage(entry);
    if (!installed) {
      setPendingScenario({ entry, scenarioId });
      return;
    }
    updateSelection({
      targetLanguage: setup.targetLanguage,
      level: entry.level,
      domain: entry.domain,
      scenarioId,
    });
    setActiveScenario(resolveWebScenario(installed, scenarioId, setup.supportLanguage));
  };
  const installPending = async () => {
    if (!pendingScenario || !setup || installing) return;
    const requestId = installRequestId.current + 1;
    installRequestId.current = requestId;
    const requestedScenario = pendingScenario;
    setInstalling(true);
    setInstallProgress(null);
    setError(null);
    try {
      const installed = await installWebContentPackage(
        requestedScenario.entry,
        (progress) => {
          if (installRequestId.current === requestId) {
            setInstallProgress(progress);
          }
        },
      );
      if (installRequestId.current !== requestId) {
        return;
      }
      const resolved = resolveWebScenario(installed, requestedScenario.scenarioId, setup.supportLanguage);
      updateSelection({
        targetLanguage: setup.targetLanguage,
        level: requestedScenario.entry.level,
        domain: requestedScenario.entry.domain,
        scenarioId: requestedScenario.scenarioId,
      });
      setActiveScenario(resolved);
      setInstalledPackages((current) => [
        ...current.filter((item) => item.entry.packageId !== installed.entry.packageId),
        installed,
      ]);
      setInstalledStatus("ready");
      setPendingScenario(null);
    } catch {
      if (installRequestId.current === requestId) {
        setError("package_install_failed");
      }
    } finally {
      if (installRequestId.current === requestId) {
        setInstalling(false);
      }
    }
  };
  const closeInstall = () => {
    installRequestId.current += 1;
    setInstalling(false);
    setInstallProgress(null);
    setPendingScenario(null);
    setActiveScenario(null);
    if (setup) {
      updateSelection({ ...selection, targetLanguage: setup.targetLanguage, scenarioId: null });
    }
  };

  return {
    activeScenario,
    back,
    catalog,
    chooseDomain,
    chooseLevel,
    entries,
    error,
    installPending,
    installProgress,
    installing,
    installedScenarios: installedPackages.flatMap((installed) => installed.entry.scenarios.map((metadata) => (
      resolveWebScenario(installed, metadata.scenarioId, setup?.supportLanguage || "en")
    ))),
    installedStatus,
    openDomainPicker,
    openLevelPicker,
    openScenario,
    openScenarioPicker,
    pendingScenario,
    retry: () => setLoadAttempt((current) => current + 1),
    selection,
    status,
    closeInstall,
  };
}
